import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { captureReferralCode } from '../utils/referralTracking';
import './LandingPage.css';

/* ─────────────────────────────────────────────
   SVG Icon Components (inline for zero-dep)
   ───────────────────────────────────────────── */

function IconDollar({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function IconShield({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function IconTrendingUp({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function IconUsers({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconFileText({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function IconZap({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconCheckCircle({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function IconBarChart({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}

function IconArrowRight({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function IconMenu({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function IconX({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ─────────────────────────────────────────────
   Hero Network - SVG connecting lines
   ───────────────────────────────────────────── */

function HeroNetwork() {
  return (
    <div className="landing-hero-network">
      <svg
        className="landing-network-svg"
        viewBox="0 0 1200 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Connecting lines */}
        <line x1="180" y1="160" x2="340" y2="100" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="340" y1="100" x2="520" y2="60" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="520" y1="60" x2="600" y2="160" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="340" y1="100" x2="600" y2="160" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="600" y1="160" x2="420" y2="220" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="600" y1="160" x2="860" y2="100" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="860" y1="100" x2="1020" y2="160" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="600" y1="160" x2="1020" y2="160" stroke="#E8E8EA" strokeWidth="1.5" />
        <line x1="1020" y1="160" x2="860" y2="240" stroke="#E8E8EA" strokeWidth="1.5" />
        {/* Connection dots */}
        <circle cx="420" cy="130" r="3.5" fill="#9B7AE8" />
        <circle cx="750" cy="120" r="3.5" fill="#9B7AE8" />
        <circle cx="500" cy="190" r="3.5" fill="#9B7AE8" />
        <circle cx="930" cy="130" r="3.5" fill="#9B7AE8" />
        <circle cx="940" cy="200" r="3.5" fill="#9B7AE8" />
      </svg>

      {/* Icon Cards - positioned absolutely over the SVG */}
      <div className="landing-icon-card landing-icon-yellow" style={{ left: '10%', top: '16%' }} aria-label="Loan management">
        <IconDollar className="w-8 h-8 text-white" />
      </div>
      <div className="landing-icon-card landing-icon-sky" style={{ left: '27%', top: '52%' }} aria-label="Investor tracking">
        <IconUsers className="w-8 h-8 text-white" />
      </div>
      <div className="landing-icon-card landing-icon-card-center" style={{ left: '50%', top: '28%', transform: 'translate(-50%, -50%) scale(1.4)' }} aria-label="Security & protection">
        <IconShield className="w-10 h-10 text-white" />
      </div>
      <div className="landing-icon-card landing-icon-coral" style={{ right: '12%', top: '16%' }} aria-label="Automated workflows">
        <IconZap className="w-8 h-8 text-white" />
      </div>
      <div className="landing-icon-card landing-icon-light" style={{ right: '4%', top: '50%' }} aria-label="Performance analytics">
        <IconBarChart className="w-7 h-7" style={{ color: '#4A4A4A' }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Feature Card
   ───────────────────────────────────────────── */

interface FeatureCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  visual: React.ReactNode;
}

function FeatureCard({ icon, iconBg, title, description, visual }: FeatureCardProps) {
  return (
    <div className="landing-feature-card">
      <div className="landing-feature-visual">
        {visual}
      </div>
      <div className="landing-feature-content">
        <div className="landing-feature-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        <h3 className="landing-feature-title">{title}</h3>
        <p className="landing-feature-desc">{description}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Mini Dashboard Visuals for Feature Cards
   ───────────────────────────────────────────── */

function LoanPipelineVisual() {
  return (
    <div className="landing-visual-container" role="img" aria-label="Loan pipeline bar chart showing quarterly loan activity">
      <div className="landing-mini-card">
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>Loan Pipeline</span>
          <span style={{ fontSize: '11px', color: '#9B9B9B' }}>This Quarter</span>
        </div>
        <div className="flex gap-2 items-end" style={{ height: '80px' }}>
          {[65, 45, 80, 55, 70, 90, 60].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-md" style={{
              height: `${h}%`,
              background: i % 2 === 0 ? '#9B7AE8' : '#E8664A',
              opacity: 0.8 + (i * 0.03),
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function InvestorInsightsVisual() {
  return (
    <div className="landing-visual-container" role="img" aria-label="Investor returns dashboard showing 12.4% returns, up 2.1% from last quarter">
      <div className="landing-mini-card" style={{ position: 'relative' }}>
        <div className="flex items-center gap-2 mb-2">
          <div style={{ width: 28, height: 28, borderRadius: 8, background: '#5CC8E4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconTrendingUp className="w-4 h-4 text-white" />
          </div>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>Investor Returns</span>
        </div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1A1A1A', letterSpacing: '-1px' }}>12.4%</div>
        <div style={{ fontSize: '11px', color: '#10B981', fontWeight: 500 }}>+2.1% vs last quarter</div>
        <svg viewBox="0 0 200 60" style={{ width: '100%', height: 50, marginTop: 8 }}>
          <path d="M0 50 Q30 40 50 35 T100 20 T150 25 T200 5" fill="none" stroke="#9B7AE8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M0 50 Q30 40 50 35 T100 20 T150 25 T200 5 L200 60 L0 60 Z" fill="url(#purpleFade)" />
          <defs><linearGradient id="purpleFade" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9B7AE8" stopOpacity="0.15" /><stop offset="100%" stopColor="#9B7AE8" stopOpacity="0" /></linearGradient></defs>
        </svg>
      </div>
    </div>
  );
}

function DocumentVisual() {
  return (
    <div className="landing-visual-container" role="img" aria-label="Loan status workflow showing progression from Request to Matured">
      <div className="landing-mini-card">
        <div className="flex items-center gap-2 mb-3">
          <div style={{ width: 28, height: 28, borderRadius: 8, background: '#9B7AE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconCheckCircle className="w-4 h-4 text-white" />
          </div>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>Status Workflow</span>
        </div>
        <div className="flex flex-col gap-2">
          {[
            { label: 'Request', color: '#F5D76E', w: '100%' },
            { label: 'Approved', color: '#5CC8E4', w: '75%' },
            { label: 'Active', color: '#9B7AE8', w: '60%' },
            { label: 'Matured', color: '#10B981', w: '40%' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <span style={{ fontSize: '10px', color: '#9B9B9B', width: 52, textAlign: 'right' }}>{item.label}</span>
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#F0F0F2' }}>
                <div style={{ width: item.w, height: '100%', borderRadius: 4, background: item.color, transition: 'width 0.8s ease' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Workflow Step Cards
   ───────────────────────────────────────────── */

function WorkflowSection() {
  const steps = [
    { num: '01', title: 'Create loan records', desc: 'Set up loans with terms, rates, borrower details and property info in seconds.' },
    { num: '02', title: 'Track investors', desc: 'Manage investor contributions, fund availability, and payout preferences.' },
    { num: '03', title: 'Automate workflows', desc: 'Payment schedules, status changes, and borrower notifications on autopilot.' },
    { num: '04', title: 'Monitor performance', desc: 'Real-time dashboard with portfolio metrics, upcoming payments, and alerts.' },
  ];

  return (
    <section className="landing-section">
      <div className="landing-container">
        <div className="landing-section-header">
          <h2 className="landing-h2">How it works</h2>
          <p className="landing-subtitle">From loan request to maturity, manage the entire lifecycle in four simple steps.</p>
        </div>
        <div className="landing-workflow-grid">
          {steps.map((step) => (
            <div key={step.num} className="landing-workflow-card">
              <div className="landing-workflow-num">{step.num}</div>
              <h3 className="landing-workflow-title">{step.title}</h3>
              <p className="landing-workflow-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Main Landing Page
   ───────────────────────────────────────────── */

export function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Capture referral code from URL if user lands on /?ref=code
  useEffect(() => {
    captureReferralCode();
  }, []);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="landing-page">
      {/* ── Floating Pill Navbar ── */}
      <nav className={`landing-nav ${scrolled ? 'landing-nav-scrolled' : ''}`}>
        <div className="landing-nav-inner">
          <Link to="/" className="landing-logo"><img src="https://storage.googleapis.com/loanflow-assets/logo-horiz-transparent.png" alt="LoanFlow" className="landing-logo-img" /></Link>
          <div className="landing-nav-links">
            <a href="#features" className="landing-nav-link">Features</a>
            <a href="#workflow" className="landing-nav-link">How It Works</a>
            <a href="#pricing" className="landing-nav-link">Pricing</a>
          </div>
          <button
            className="landing-menu-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <IconX className="w-5 h-5" /> : <IconMenu className="w-5 h-5" />}
          </button>
          <div className="landing-nav-actions">
            <Link to="/login" className="landing-btn-outline">Sign In</Link>
            <Link to="/register" className="landing-btn-dark">Sign Up</Link>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      <div className={`landing-mobile-menu ${menuOpen ? 'landing-mobile-menu-open' : ''}`}>
        <a href="#features" className="landing-mobile-menu-link" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#workflow" className="landing-mobile-menu-link" onClick={() => setMenuOpen(false)}>How It Works</a>
        <a href="#pricing" className="landing-mobile-menu-link" onClick={() => setMenuOpen(false)}>Pricing</a>
      </div>

      {/* ── Hero Section ── */}
      <section className="landing-hero">
        <HeroNetwork />
        <h1 className="landing-h1">
          Private lending,<br />simplified
        </h1>
        <p className="landing-hero-subtitle">
          LoanFlow is a modern platform for private money lenders<br className="hidden sm:block" />
          to manage loans, investors, and borrower communications.
        </p>
        <div className="landing-hero-actions">
          <Link to="/register" className="landing-btn-coral">
            Get Started Free
            <IconArrowRight className="w-4 h-4 ml-2 inline-block" />
          </Link>
          <Link to="/login" className="landing-btn-ghost">Sign In</Link>
        </div>
      </section>

      {/* ── Social Proof Stats (hidden) ── */}
      {/* <section className="landing-stats-bar">
        <div className="landing-container">
          <div className="landing-stats-grid">
            <AnimatedStat value={500} suffix="+" label="Active Lenders" />
            <AnimatedStat value={2} suffix="B+" label="Loans Managed" />
            <AnimatedStat value={98} suffix="%" label="Customer Satisfaction" />
            <AnimatedStat value={15} suffix="min" label="Avg. Setup Time" />
          </div>
        </div>
      </section> */}

      {/* ── Built for Everyone (Feature Cards) ── */}
      <section id="features" className="landing-section">
        <div className="landing-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Built for every lender</h2>
            <p className="landing-subtitle">
              Whether you manage 3 loans or 300, LoanFlow adapts<br className="hidden sm:block" />
              to your private lending business.
            </p>
          </div>
          <div className="landing-features-grid">
            <FeatureCard
              icon={<IconDollar className="w-5 h-5 text-white" />}
              iconBg="#E8664A"
              title="For solo lenders"
              description="Track every loan from request to maturity with payment schedules, milestones, and real-time status updates."
              visual={<LoanPipelineVisual />}
            />
            <FeatureCard
              icon={<IconUsers className="w-5 h-5 text-white" />}
              iconBg="#5CC8E4"
              title="For fund managers"
              description="Manage multiple investors per loan, track contributions, and generate payout reports automatically."
              visual={<InvestorInsightsVisual />}
            />
            <FeatureCard
              icon={<IconFileText className="w-5 h-5 text-white" />}
              iconBg="#9B7AE8"
              title="For growing firms"
              description="Custom intake forms, automated borrower notifications, and a full activity log for every loan."
              visual={<DocumentVisual />}
            />
          </div>
        </div>
      </section>

      {/* ── Workflow Section ── */}
      <div id="workflow">
        <WorkflowSection />
      </div>

      {/* ── Integration / Trust Section ── */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-trust-card">
            <div className="landing-trust-icon-wrap">
              <IconShield className="w-7 h-7" style={{ color: '#E8664A' }} />
            </div>
            <h2 className="landing-h2" style={{ marginBottom: 16 }}>
              Your data, always secure
            </h2>
            <p className="landing-subtitle" style={{ marginBottom: 48 }}>
              Multi-tenant isolation ensures your loan data is completely separated.<br className="hidden md:block" />
              Each lender gets their own secure workspace.
            </p>
            <div className="landing-trust-features">
              {[
                'Tenant-isolated data',
                'Encrypted connections',
                'Session-based auth',
                'Role-based access',
                'Audit trail logging',
                'Automated backups',
              ].map((feature) => (
                <div key={feature} className="landing-trust-item">
                  <IconCheckCircle className="w-5 h-5" style={{ color: '#10B981', flexShrink: 0 }} />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Section ── */}
      <section id="pricing" className="landing-section landing-pricing-section">
        <div className="landing-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Simple, transparent pricing</h2>
            <p className="landing-subtitle">
              Start free. Upgrade when your portfolio grows.<br className="hidden sm:block" />
              No credit card required.
            </p>
          </div>
          <div className="landing-pricing-grid">
            {/* Starter Plan */}
            <div className="landing-pricing-card">
              <div className="landing-pricing-card-header">
                <span className="landing-pricing-plan-name">Starter</span>
                <div className="landing-pricing-price">
                  <span className="landing-pricing-amount">$0</span>
                  <span className="landing-pricing-period">/ forever</span>
                </div>
                <p className="landing-pricing-desc">Everything you need to manage your first loans.</p>
              </div>
              <div className="landing-pricing-features">
                {[
                  '3 active loans',
                  '1 investor',
                  '1 intake form',
                  'Borrower portal',
                  'Payment tracking',
                  'Email notifications',
                ].map((feature) => (
                  <div key={feature} className="landing-pricing-feature">
                    <IconCheckCircle className="w-[18px] h-[18px]" style={{ color: 'var(--lf-success)', flexShrink: 0 }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/register" className="landing-pricing-btn landing-pricing-btn-outline">
                Get Started Free
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="landing-pricing-card landing-pricing-card-featured">
              <div className="landing-pricing-badge">Most Popular</div>
              <div className="landing-pricing-card-header">
                <span className="landing-pricing-plan-name">Pro</span>
                <div className="landing-pricing-price">
                  <span className="landing-pricing-amount">$79</span>
                  <span className="landing-pricing-period">/ month</span>
                </div>
                <p className="landing-pricing-desc">Unlimited everything for growing lending businesses.</p>
              </div>
              <div className="landing-pricing-features">
                {[
                  'Unlimited active loans',
                  'Unlimited investors',
                  'Unlimited intake forms',
                  'Custom branding',
                  'Custom domain',
                  'Cross-collateral loans',
                  'Priority support',
                ].map((feature) => (
                  <div key={feature} className="landing-pricing-feature">
                    <IconCheckCircle className="w-[18px] h-[18px]" style={{ color: 'var(--lf-coral)', flexShrink: 0 }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/register?plan=pro" className="landing-pricing-btn landing-pricing-btn-coral">
                Get Pro
                <IconArrowRight className="w-4 h-4 ml-2 inline-block" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer-inner">
            <div className="landing-footer-brand">
              <span className="landing-logo"><img src="https://storage.googleapis.com/loanflow-assets/logo-horiz-transparent.png" alt="LoanFlow" className="landing-footer-logo-img" /></span>
              <p className="landing-footer-tagline">Private money lending, simplified.</p>
            </div>
            <div className="landing-footer-links">
              <div>
                <h4 className="landing-footer-heading">Product</h4>
                <a href="#features" className="landing-footer-link">Features</a>
                <a href="#workflow" className="landing-footer-link">How It Works</a>
                <a href="#pricing" className="landing-footer-link">Pricing</a>
              </div>
              <div>
                <h4 className="landing-footer-heading">Account</h4>
                <Link to="/login" className="landing-footer-link">Sign In</Link>
                <Link to="/register" className="landing-footer-link">Sign Up</Link>
              </div>
            </div>
          </div>
          <div className="landing-footer-bottom">
            <span>&copy; {new Date().getFullYear()} LoanFlow. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
